import { useRef, MutableRefObject, RefCallback, ReactElement } from 'react';
import { useDrag, DragSourceMonitor } from 'react-dnd';
import { SourceType } from 'dnd-core';

export interface DraggableItem<T extends object = object> {
  /** Draggable item id */
  id: string;
  /** Draggable item type */
  type: SourceType;
  /** Arbitrary data associated with this drag item */
  data?: T;
}

export interface DraggableCollectedProps {
  isDragging: boolean;
  dragEl: HTMLElement | null;
}

export interface DraggableProps<T extends object = object> extends DraggableItem<T> {
  onBegin?: (monitor: DragSourceMonitor) => object;
  /** Called when drag item is dropped on a valid drop target */
  onEnd?: (item: DraggableItem<T> | undefined, monitor: DragSourceMonitor) => void;
  children: (props: {
    dragRef: RefCallback<HTMLElement>;
    previewRef: RefCallback<HTMLElement>;
    collected: DraggableCollectedProps;
  }) => ReactElement;
}

const Draggable = <T extends object = object>({
  id,
  type,
  data,
  onBegin,
  onEnd,
  children
}: DraggableProps<T>) => {
  const dragElRef: MutableRefObject<HTMLElement | null> = useRef<HTMLElement>(null);

  const [collected, dragRef, previewRef] = useDrag(
    () => ({
      type,
      item: monitor => ({ ...onBegin?.(monitor), id, type, data }),
      end: onEnd,
      collect: monitor => ({
        isDragging: monitor.isDragging(),
        dragEl: dragElRef.current
      })
    }),
    [id, type, data, onBegin, onEnd]
  );

  return children({
    dragRef: (el: HTMLElement) => {
      dragElRef.current = el;
      dragRef(el);
    },
    previewRef,
    collected
  });
};

export default Draggable;
